/**
 * NextLeadUpPage
 * 
 * Main page for the Hot Lead Queue - a gamified lead distribution system
 * for portfolio refinance opportunities.
 * 
 * Features:
 * - Real-time queue of available leads sorted by deal score
 * - Semi-blind lead cards (PII hidden until claimed)
 * - Session statistics and claim capacity tracking
 * - Anti-hoarding gating (must work existing leads to claim new ones)
 * - Filters by state, opportunity type, score range
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Flex,
  HStack,
  VStack,
  Badge,
  Icon,
  useColorModeValue,
  useToast,
  Alert,
  AlertIcon,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
} from '@chakra-ui/react';
import { Link, useHistory } from 'react-router-dom';
import { FiZap, FiChevronRight, FiTrendingUp } from 'react-icons/fi';

// Auth context
import { useAuth } from '../../contexts/AuthContext';

// Custom hook
import { useHotLeadQueue } from '../../hooks/useHotLeadQueue';

// Components
import QueueStatsHeader from '../../components/CRM/HotLeadQueue/QueueStatsHeader';
import QueueFilters from '../../components/CRM/HotLeadQueue/QueueFilters';
import LeadQueueList from '../../components/CRM/HotLeadQueue/LeadQueueList';
import ClaimGateModal from '../../components/CRM/HotLeadQueue/ClaimGateModal';

export function NextLeadUpPage() {
  const history = useHistory();
  const toast = useToast();
  const { currentUser, Roles, SuperAdmin } = useAuth();
  
  // Filter state
  const [filters, setFilters] = useState({});
  
  // Gate modal state
  const [showGateModal, setShowGateModal] = useState(false);
  
  // Colors
  const pageBg = useColorModeValue('gray.50', 'gray.900');
  const headerBg = useColorModeValue('white', 'gray.800');

  // Check access - SuperAdmin, CRM Admin, or LOs with CRMUser flag
  const isCRMAdmin = SuperAdmin || Roles?.includes('CRM Admin');
  const canAccessQueue = isCRMAdmin || (currentUser?.CRMUser === true && 
    (Roles?.includes('Loan Officer') || Roles?.includes('DTC Loan Officer')));

  // Use the hot lead queue hook
  const {
    availableLeads,
    loading,
    error,
    hasMore,
    totalCount,
    userStats,
    claimLead,
    claimingLeadId,
    claimResult,
    clearClaimResult,
    loadMore,
    refresh,
  } = useHotLeadQueue(currentUser?.uid, filters);

  // Handle filter changes
  const handleFilterChange = useCallback((newFilters) => {
    setFilters(newFilters);
  }, []);

  // Handle claim action
  const handleClaim = useCallback(async (leadId) => {
    const result = await claimLead(leadId);
    
    if (result.success) {
      // Success - show toast and navigate to deal
      toast({
        title: 'Lead Claimed!',
        description: `You've claimed ${result.lead?.borrowerName || 'a new lead'}. Opening deal page...`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      // Navigate to deal page after brief delay
      setTimeout(() => {
        history.push(`/crm/deals/${result.dealId}`);
      }, 1500);
    } else if (result.blocked) {
      // Blocked - show gate modal
      setShowGateModal(true);
    } else {
      // Other error
      toast({
        title: 'Claim Failed',
        description: result.error || result.message || 'Failed to claim lead',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [claimLead, history, toast]);

  // Handle claim result changes (for blocked claims from hook)
  useEffect(() => {
    if (claimResult?.blocked) {
      setShowGateModal(true);
    }
  }, [claimResult]);

  // Close gate modal
  const handleCloseGateModal = useCallback(() => {
    setShowGateModal(false);
    clearClaimResult();
  }, [clearClaimResult]);

  // Access denied
  if (!canAccessQueue) {
    return (
      <Container maxW="container.lg" py={8}>
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          <Box>
            <Text fontWeight="bold">Access Restricted</Text>
            <Text fontSize="sm">
              Only Loan Officers can access the Hot Lead Queue. 
              Please contact your administrator if you believe this is an error.
            </Text>
          </Box>
        </Alert>
      </Container>
    );
  }

  return (
    <Box minH="100vh" bg={pageBg}>
      {/* Header */}
      <Box bg={headerBg} borderBottomWidth="1px" py={4} px={6} mb={6}>
        <Container maxW="container.xl">
          {/* Breadcrumb */}
          <Breadcrumb 
            spacing="8px" 
            separator={<FiChevronRight color="gray.500" />}
            fontSize="sm"
            color="gray.500"
            mb={3}
          >
            <BreadcrumbItem>
              <BreadcrumbLink as={Link} to="/crm">CRM</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem isCurrentPage>
              <BreadcrumbLink>Next Lead Up</BreadcrumbLink>
            </BreadcrumbItem>
          </Breadcrumb>

          {/* Title */}
          <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
            <HStack spacing={3}>
              <Icon as={FiZap} boxSize={8} color="blue.500" />
              <VStack align="start" spacing={0}>
                <Heading size="lg">Next Lead Up</Heading>
                <Text fontSize="sm" color="gray.500">
                  High-velocity lead claiming for portfolio refinance opportunities
                </Text>
              </VStack>
            </HStack>
            
            <HStack spacing={3}>
              <Badge colorScheme="green" fontSize="md" px={3} py={1} borderRadius="full">
                <HStack spacing={1}>
                  <Icon as={FiTrendingUp} />
                  <Text>{totalCount} Available</Text>
                </HStack>
              </Badge>
            </HStack>
          </Flex>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxW="container.xl" pb={8}>
        {/* Error Alert */}
        {error && (
          <Alert status="error" mb={4} borderRadius="md">
            <AlertIcon />
            {error}
          </Alert>
        )}

        {/* Stats Header */}
        <QueueStatsHeader stats={userStats} loading={loading} />

        {/* Filters */}
        <QueueFilters 
          filters={filters}
          onFilterChange={handleFilterChange}
          onRefresh={refresh}
          loading={loading}
        />

        {/* Lead Queue List */}
        <LeadQueueList
          leads={availableLeads}
          loading={loading}
          hasMore={hasMore}
          onLoadMore={loadMore}
          onClaim={handleClaim}
          claimingLeadId={claimingLeadId}
          isBlocked={userStats?.isBlocked}
          emptyMessage={
            Object.keys(filters).length > 0
              ? 'No leads match your filters. Try adjusting your criteria.'
              : 'No leads available in the queue right now.'
          }
        />
      </Container>

      {/* Gate Modal */}
      <ClaimGateModal
        isOpen={showGateModal}
        onClose={handleCloseGateModal}
        blockedBy={claimResult?.blockedBy || userStats?.pendingUnlock || []}
        stats={claimResult?.stats || userStats}
        message={claimResult?.message}
      />
    </Box>
  );
}

export default NextLeadUpPage;
